import { useState } from 'react'
import { Article } from '../hooks/useArticles'
import { useAIExtracts, SharedExtract } from '../hooks/useAIExtracts'
import { ExtractType, KeyPoint, Flashcard, AISummary } from '../lib/supabase'

interface ShareExtractProps {
  article: Article
  userId: string | undefined
  onClose: () => void
}

type Tab = 'summary' | 'key_points' | 'flashcards'

export function ShareExtract({ article, userId, onClose }: ShareExtractProps) {
  const [activeTab, setActiveTab] = useState<Tab>('summary')
  const [sharedExtract, setSharedExtract] = useState<SharedExtract | null>(null)
  const [copied, setCopied] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<AISummary | null>(null)

  const {
    loading,
    error,
    generateSummary,
    shareExtract,
    exportAsMarkdown,
    exportForTwitter,
    exportForLinkedIn: _exportForLinkedIn,
    copyToClipboard
  } = useAIExtracts(userId)

  const handleGenerate = async () => {
    const summary = await generateSummary(article)
    if (summary) {
      setGeneratedContent(summary)
    }
  }

  const handleShare = async () => {
    if (!generatedContent) return

    let content: string | KeyPoint[] | Flashcard[]
    let type: ExtractType

    switch (activeTab) {
      case 'summary':
        content = generatedContent.detailed
        type = 'summary'
        break
      case 'key_points':
        content = generatedContent.keyPoints
        type = 'key_points'
        break
      case 'flashcards':
        content = generatedContent.flashcards
        type = 'flashcards'
        break
    }

    const shared = await shareExtract(article, type, content)
    if (shared) {
      setSharedExtract(shared)
    }
  }

  const handleCopyLink = async () => {
    if (sharedExtract) {
      const success = await copyToClipboard(sharedExtract.shareUrl)
      if (success) {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    }
  }

  const handleCopyMarkdown = async () => {
    if (sharedExtract) {
      const markdown = exportAsMarkdown(sharedExtract)
      const success = await copyToClipboard(markdown)
      if (success) {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    }
  }

  const handleShareTwitter = () => {
    if (sharedExtract) {
      const text = exportForTwitter(sharedExtract)
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`
      window.open(url, '_blank', 'width=550,height=420')
    }
  }

  const handleShareLinkedIn = () => {
    if (sharedExtract) {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(sharedExtract.shareUrl)}`
      window.open(url, '_blank', 'width=550,height=420')
    }
  }

  const renderContent = () => {
    if (!generatedContent) {
      return (
        <div className="generate-prompt">
          <div className="ai-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <h3>Generate AI Content</h3>
          <p>Create shareable summaries, key points, and flashcards from this article.</p>
          <button
            className="generate-btn"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner" />
                Generating...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                Generate with AI
              </>
            )}
          </button>
        </div>
      )
    }

    switch (activeTab) {
      case 'summary':
        return (
          <div className="content-preview">
            <h4>Summary</h4>
            <p className="brief">{generatedContent.brief}</p>
            <div className="detailed">
              <h5>Detailed</h5>
              <p>{generatedContent.detailed}</p>
            </div>
          </div>
        )
      case 'key_points':
        return (
          <div className="content-preview">
            <h4>Key Points</h4>
            <ul className="key-points-list">
              {generatedContent.keyPoints.map((kp, i) => (
                <li key={kp.id} className={`importance-${kp.importance}`}>
                  <span className="number">{i + 1}</span>
                  <span className="text">{kp.text}</span>
                  <span className={`badge ${kp.importance}`}>{kp.importance}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      case 'flashcards':
        return (
          <div className="content-preview">
            <h4>Flashcards</h4>
            <div className="flashcards-list">
              {generatedContent.flashcards.map((fc, i) => (
                <div key={fc.id} className="flashcard">
                  <div className="card-number">Card {i + 1}</div>
                  <div className="front">
                    <span className="label">Q:</span>
                    {fc.front}
                  </div>
                  <div className="back">
                    <span className="label">A:</span>
                    {fc.back}
                  </div>
                  {fc.tags && fc.tags.length > 0 && (
                    <div className="tags">
                      {fc.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="share-extract-overlay" onClick={onClose}>
      <div className="share-extract-modal" onClick={e => e.stopPropagation()}>
        <header className="modal-header">
          <h2>Share AI Extract</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="article-info">
          <h3>{article.title}</h3>
          <span className="reading-time">{article.readingTime} min read</span>
        </div>

        {generatedContent && (
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              Summary
            </button>
            <button
              className={`tab ${activeTab === 'key_points' ? 'active' : ''}`}
              onClick={() => setActiveTab('key_points')}
            >
              Key Points
            </button>
            <button
              className={`tab ${activeTab === 'flashcards' ? 'active' : ''}`}
              onClick={() => setActiveTab('flashcards')}
            >
              Flashcards
            </button>
          </div>
        )}

        <div className="content-area">
          {error && <div className="error-message">{error}</div>}
          {renderContent()}
        </div>

        {generatedContent && !sharedExtract && (
          <div className="share-actions">
            <button
              className="share-btn primary"
              onClick={handleShare}
              disabled={loading}
            >
              {loading ? 'Creating link...' : 'Create Share Link'}
            </button>
          </div>
        )}

        {sharedExtract && (
          <div className="share-result">
            <div className="share-link-container">
              <input
                type="text"
                readOnly
                value={sharedExtract.shareUrl}
                className="share-link-input"
              />
              <button className="copy-btn" onClick={handleCopyLink}>
                {copied ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                )}
              </button>
            </div>

            <div className="export-options">
              <h4>Export & Share</h4>
              <div className="export-buttons">
                <button className="export-btn" onClick={handleCopyMarkdown}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                  </svg>
                  Copy as Markdown
                </button>
                <button className="export-btn twitter" onClick={handleShareTwitter}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Share on X
                </button>
                <button className="export-btn linkedin" onClick={handleShareLinkedIn}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  Share on LinkedIn
                </button>
              </div>
            </div>

            <div className="branding">
              <span>Generated by Read Later AI</span>
            </div>
          </div>
        )}

        <style>{`
          .share-extract-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            z-index: 1000;
            animation: fadeIn 0.2s ease;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .share-extract-modal {
            background: var(--bg-secondary, #1a1a1a);
            border-radius: 16px;
            width: 100%;
            max-width: 500px;
            max-height: 90vh;
            overflow-y: auto;
            animation: slideUp 0.3s ease;
          }

          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid var(--border-color, #333);
          }

          .modal-header h2 {
            font-size: 1.125rem;
            font-weight: 600;
            margin: 0;
          }

          .close-btn {
            background: none;
            border: none;
            color: var(--text-secondary, #888);
            cursor: pointer;
            padding: 0.25rem;
            border-radius: 4px;
            transition: color 0.2s;
          }

          .close-btn:hover {
            color: var(--text-primary, #fff);
          }

          .article-info {
            padding: 1rem 1.25rem;
            border-bottom: 1px solid var(--border-color, #333);
          }

          .article-info h3 {
            font-size: 0.875rem;
            font-weight: 500;
            margin: 0 0 0.25rem;
            color: var(--text-primary, #fff);
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .article-info .reading-time {
            font-size: 0.75rem;
            color: var(--text-secondary, #888);
          }

          .tabs {
            display: flex;
            border-bottom: 1px solid var(--border-color, #333);
          }

          .tab {
            flex: 1;
            padding: 0.75rem 1rem;
            background: none;
            border: none;
            color: var(--text-secondary, #888);
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            border-bottom: 2px solid transparent;
          }

          .tab:hover {
            color: var(--text-primary, #fff);
          }

          .tab.active {
            color: var(--accent, #f97316);
            border-bottom-color: var(--accent, #f97316);
          }

          .content-area {
            padding: 1.25rem;
            min-height: 200px;
          }

          .generate-prompt {
            text-align: center;
            padding: 2rem 1rem;
          }

          .ai-icon {
            color: var(--accent, #f97316);
            margin-bottom: 1rem;
          }

          .generate-prompt h3 {
            font-size: 1.125rem;
            margin: 0 0 0.5rem;
          }

          .generate-prompt p {
            color: var(--text-secondary, #888);
            font-size: 0.875rem;
            margin: 0 0 1.5rem;
          }

          .generate-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1.5rem;
            background: var(--accent, #f97316);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          }

          .generate-btn:hover:not(:disabled) {
            background: #ea580c;
          }

          .generate-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .spinner {
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .content-preview h4 {
            font-size: 0.875rem;
            font-weight: 600;
            margin: 0 0 0.75rem;
            color: var(--accent, #f97316);
          }

          .content-preview h5 {
            font-size: 0.75rem;
            font-weight: 600;
            margin: 1rem 0 0.5rem;
            color: var(--text-secondary, #888);
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .content-preview p {
            font-size: 0.875rem;
            line-height: 1.6;
            color: var(--text-primary, #fff);
            margin: 0;
          }

          .content-preview .brief {
            font-weight: 500;
          }

          .key-points-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .key-points-list li {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 0.75rem;
            background: var(--bg-tertiary, #252525);
            border-radius: 8px;
            margin-bottom: 0.5rem;
          }

          .key-points-list .number {
            flex-shrink: 0;
            width: 24px;
            height: 24px;
            background: var(--accent, #f97316);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.75rem;
            font-weight: 600;
          }

          .key-points-list .text {
            flex: 1;
            font-size: 0.875rem;
            line-height: 1.5;
          }

          .key-points-list .badge {
            flex-shrink: 0;
            font-size: 0.625rem;
            text-transform: uppercase;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-weight: 600;
          }

          .badge.high {
            background: rgba(239, 68, 68, 0.2);
            color: #ef4444;
          }

          .badge.medium {
            background: rgba(249, 115, 22, 0.2);
            color: #f97316;
          }

          .badge.low {
            background: rgba(34, 197, 94, 0.2);
            color: #22c55e;
          }

          .flashcards-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .flashcard {
            background: var(--bg-tertiary, #252525);
            border-radius: 8px;
            padding: 1rem;
          }

          .flashcard .card-number {
            font-size: 0.75rem;
            color: var(--accent, #f97316);
            font-weight: 600;
            margin-bottom: 0.5rem;
          }

          .flashcard .front,
          .flashcard .back {
            font-size: 0.875rem;
            line-height: 1.5;
            margin-bottom: 0.5rem;
          }

          .flashcard .label {
            font-weight: 600;
            margin-right: 0.5rem;
          }

          .flashcard .front .label {
            color: var(--accent, #f97316);
          }

          .flashcard .back .label {
            color: #22c55e;
          }

          .flashcard .tags {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
            margin-top: 0.5rem;
          }

          .flashcard .tag {
            font-size: 0.625rem;
            padding: 0.25rem 0.5rem;
            background: var(--bg-primary, #0a0a0a);
            border-radius: 4px;
            color: var(--text-secondary, #888);
          }

          .error-message {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
            padding: 0.75rem 1rem;
            border-radius: 8px;
            font-size: 0.875rem;
            margin-bottom: 1rem;
          }

          .share-actions {
            padding: 1rem 1.25rem;
            border-top: 1px solid var(--border-color, #333);
          }

          .share-btn.primary {
            width: 100%;
            padding: 0.875rem;
            background: var(--accent, #f97316);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          }

          .share-btn.primary:hover:not(:disabled) {
            background: #ea580c;
          }

          .share-btn.primary:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .share-result {
            padding: 1rem 1.25rem;
            border-top: 1px solid var(--border-color, #333);
          }

          .share-link-container {
            display: flex;
            gap: 0.5rem;
            margin-bottom: 1.25rem;
          }

          .share-link-input {
            flex: 1;
            padding: 0.75rem;
            background: var(--bg-tertiary, #252525);
            border: 1px solid var(--border-color, #333);
            border-radius: 8px;
            color: var(--text-primary, #fff);
            font-size: 0.875rem;
          }

          .copy-btn {
            padding: 0.75rem;
            background: var(--accent, #f97316);
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.2s;
          }

          .copy-btn:hover {
            background: #ea580c;
          }

          .export-options h4 {
            font-size: 0.75rem;
            font-weight: 600;
            color: var(--text-secondary, #888);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin: 0 0 0.75rem;
          }

          .export-buttons {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .export-btn {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            background: var(--bg-tertiary, #252525);
            border: 1px solid var(--border-color, #333);
            border-radius: 8px;
            color: var(--text-primary, #fff);
            font-size: 0.875rem;
            cursor: pointer;
            transition: all 0.2s;
          }

          .export-btn:hover {
            background: var(--bg-primary, #0a0a0a);
            border-color: var(--accent, #f97316);
          }

          .export-btn.twitter:hover {
            border-color: #1d9bf0;
          }

          .export-btn.linkedin:hover {
            border-color: #0a66c2;
          }

          .branding {
            text-align: center;
            padding-top: 1rem;
            margin-top: 1rem;
            border-top: 1px solid var(--border-color, #333);
            font-size: 0.75rem;
            color: var(--text-secondary, #888);
          }
        `}</style>
      </div>
    </div>
  )
}
